import _get from 'lodash/get'
import { fromJS, List, Map } from 'immutable'
import _isEmpty from 'lodash/isEmpty'
import { get as getConfig } from '../../config/index'
import { containsUtm } from '../inbound-tracking/index'
import { SET_USER_DATA_LANGUAGE_PRIMARY, SET_USER_DATA_LANGUAGE } from '../user/actions'
import { SET_INBOUND_TRACKING_DATA_INITIALIZED } from '../inbound-tracking/actions'
import { SET_EVENT_PAGE_VIEWED, setEventDefault } from '../event-tracking/actions'
import { SET_APP_STORE_INITIALIZED } from '../app/actions'
import { SET_CHECKOUT_STEP, SET_CHECKOUT_ORDER_DATA } from '../checkout/actions'
import { SET_PLANS_SELECTION } from '../plans/actions'
import { SET_RESOLVER_LOCATION } from '../resolver/actions'
import { SET_AUTH_DATA } from '../auth/actions'
import { createAbsolute } from '../url/index'
import { SET_PAYTRACK_DATA_LAST_TRANSACTION } from '../paytrack/actions'
import { SET_ONBOARDING_COMPLETED } from '../onboarding/actions'
import { OPTIMIZELY_FEATURE_TEST_ENROLLMENT_GA4_EVENT } from '../event-tracking/index'
import { SET_USER_ACCOUNT_DATA_BILLING_SUBSCRIPTIONS_WITH_DETAILS } from '../user-account/actions'
import {
  SET_OPTIMIZELY_FEATURE_MOUNTED,
  SET_OPTIMIZELY_FEATURE_FLAG,
  setOptimizelyFeatureDecision,
  setOptimizelyFeatureUser,
  updateOptimizelyFeatureUser,
  SET_OPTIMIZELY_FEATURE_EVENT,
} from './actions'
import {
  getOptimizelyFeatureClient,
  getOptimizelyFeatureUser,
  formatUserHasPlanSelectedAttribute,
  getUserHasPlanSelected,
  parseUserHasPlanSelectedAttribute,
  setOptimizelyFeatureEvent,
} from '.'

const config = getConfig()
const optimizelyEnabled = !!_get(config, ['features', 'optimizelyFeatureTesting'])

/** Latest setUpdatedVariation call; only the newest onReady callback may decide. */
let decideGeneration = 0

/**
 * True when decide() should run for this flag given stored state and user context.
 * @param {import('immutable').Map} storedDecision - The stored flag decision from Redux
 * @param {object} user - Current Optimizely user from getOptimizelyFeatureUser
 * @param {object|undefined} optimizelyUser - User last passed to the Optimizely SDK
 * @returns {boolean}
 */
function shouldApplyDecision (storedDecision, user, optimizelyUser) {
  if (storedDecision.isEmpty()) {
    return true
  }

  if (!optimizelyUser) {
    return true
  }

  const storedVariation = storedDecision.get('variation')
  const isEnrolled = storedDecision.get('enabled') === true && !!storedVariation

  if (isEnrolled) {
    const prevHasPlan = parseUserHasPlanSelectedAttribute(
      optimizelyUser.attributes?.user_has_plan_selected,
    )
    const nextHasPlan = parseUserHasPlanSelectedAttribute(
      user.attributes.user_has_plan_selected,
    )

    // Re-evaluate when the user selects a plan and falls out of the audience.
    return prevHasPlan !== nextHasPlan
  }

  // Not enrolled yet (enabled:false or no variation) — retry as page_path/SDK context settles.
  return true
}

/**
 * Initialize the Optimizely feature user in the store.
 * This function retrieves the user data from the state and dispatches an action
 * to set the Optimizely feature user.
 * @param {object} store - redux store
 * @returns {void}
 */
function initializeUser (store) {
  const state = store.getState()
  const user = getOptimizelyFeatureUser(state)
  store.dispatch(setOptimizelyFeatureUser(user))
}

/**
 * Set the user authentication status in the Optimizely feature user attributes.
 * This function retrieves the authentication status and user ID from the action payload
 * and dispatches an action to update the Optimizely feature user attributes.
 * @param {object} store - redux store
 * @param {object} action - redux action containing user data
 * @returns {void}
 */
function setUserAuth (store) {
  const state = store.getState()
  const user = getOptimizelyFeatureUser(state)
  store.dispatch(updateOptimizelyFeatureUser(user))
}

/**
 * Set the plans selection in the Optimizely feature user attributes.
 * This function retrieves the user ID and SKU from the action payload
 * and dispatches an action to set the Optimizely feature event for plan selection.
 * @param {object} store - redux store
 * @param {object} action - redux action containing plan selection data
 * @returns {void}
 */
function setPlansSelection (store, action) {
  const state = store.getState()
  const { optimizelyFeature = Map() } = state
  const userId = optimizelyFeature.getIn(['user', 'id'])
  const data = _get(action, ['payload', 'data'])
  const plan = fromJS(data)
  const sku = plan.get('sku')

  if (userId && sku) {
    setOptimizelyFeatureEvent('plan_selection', userId, { SKU: sku, plan_type: plan.get('type') })
  }
}

/**
 * Set the user language attribute in the Optimizely feature user attributes.
 * This function retrieves the language from the action payload and dispatches
 * an action to update the Optimizely feature user with the language.
 * @param {object} store - redux store
 * @param {object} action - redux action containing language data
 * @returns {void}
 */
function setUserLanguageAttribute (store, action) {
  const language = _get(action, ['payload', 'value'], null)
  if (List.isList(language)) {
    store.dispatch(updateOptimizelyFeatureUser({ user_language: language.first() }))
  }
}

/**
 * Add inbound tracking attributes to the Optimizely feature user.
 * This function checks if the inbound tracking data contains UTM parameters
 * and updates the Optimizely feature user attributes accordingly.
 * @param {object} store - redux store
 * @param {object} action - redux action containing inbound tracking data
 * @returns {void}
 */
function setInboundTrackingAttributes (store, action) {
  const data = _get(action, ['payload', 'data'], Map())
  const dataContainsUtm = containsUtm(data)
  store.dispatch(updateOptimizelyFeatureUser({
    user_tracked: dataContainsUtm ? 'true' : 'false',
  }))
}

/**
 * Apply Optimizely flag decisions for the current user and mounted flags.
 * @param {object} store - redux store
 * @param {object} optimizely - The Optimizely client instance
 * @param {object} user - The Optimizely user object from getOptimizelyFeatureUser
 * @param {import('immutable').Map} flags - Registered feature flags from Redux
 * @returns {void}
 */
function applyFlagDecisions (store, optimizely, user, flags) {
  let activeFlags = List()

  flags.keySeq().forEach((flagKey) => {
    const storedDecision = flags.get(flagKey) || Map()

    if (!shouldApplyDecision(storedDecision, user, optimizely.user)) {
      if (storedDecision.get('enabled')) {
        activeFlags = activeFlags.push(flagKey)
      }
      return
    }

    optimizely.setUser(user)
    const decision = optimizely.decide(flagKey)
    const isFlagEnabled = !!decision.enabled
    const ga4EventSent = flags.getIn([flagKey, 'ga4EventSent'], false)

    if (isFlagEnabled && !ga4EventSent) {
      setTimeout(() => {
        store.dispatch(setEventDefault(OPTIMIZELY_FEATURE_TEST_ENROLLMENT_GA4_EVENT))
      }, 1000)
    }

    if (isFlagEnabled) {
      activeFlags = activeFlags.push(flagKey)
    }

    store.dispatch(setOptimizelyFeatureDecision(
      decision,
      ga4EventSent || isFlagEnabled,
    ))
  })

  if (activeFlags.size > 0) {
    const { optimizelyFeature = Map() } = store.getState()
    const activeFlagsValue = activeFlags.join(',')
    const currentActiveFlags = optimizelyFeature.getIn(['user', 'attributes', 'user_active_flags'], '')

    if (currentActiveFlags !== activeFlagsValue) {
      store.dispatch(updateOptimizelyFeatureUser({ user_active_flags: activeFlagsValue }))
    }
  }
}

/**
 * Set the updated variation for the Optimizely feature flags.
 * Waits for the Optimizely SDK to be ready before deciding so cold loads do not
 * cache a null-variation placeholder decision.
 * @param {object} store - redux store
 * @returns {void}
 */
function setUpdatedVariation (store) {
  const state = store.getState()
  const optimizelyFeature = state.optimizelyFeature || Map()
  const isMounted = optimizelyFeature.get('mounted', false)
  const user = getOptimizelyFeatureUser(state)
  const optimizely = getOptimizelyFeatureClient()
  const hasValidOptimizelyClient = !!optimizely
  const shouldRun = hasValidOptimizelyClient && isMounted && !_isEmpty(user)

  if (!shouldRun) {
    return
  }

  const generation = decideGeneration + 1
  decideGeneration = generation

  optimizely.onReady().then(({ success }) => {
    if (!success || generation !== decideGeneration) {
      return
    }

    const latestState = store.getState()
    const latestOptimizelyFeature = latestState.optimizelyFeature || Map()

    if (!latestOptimizelyFeature.get('mounted', false)) {
      return
    }

    const latestUser = getOptimizelyFeatureUser(latestState)
    const latestFlags = latestOptimizelyFeature.get('flags', Map())

    if (_isEmpty(latestUser) || latestFlags.isEmpty()) {
      return
    }

    applyFlagDecisions(store, optimizely, latestUser, latestFlags)
  })
}

/**
 * Sync user_has_plan_selected from plans.selection into Optimizely user attributes.
 * @param {object} store - redux store
 * @returns {void}
 */
function syncUserHasPlanSelected (store) {
  const { optimizelyFeature = Map() } = store.getState()
  const nextHasPlan = getUserHasPlanSelected(store.getState())
  const prevHasPlan = parseUserHasPlanSelectedAttribute(
    optimizelyFeature.getIn(['user', 'attributes', 'user_has_plan_selected']),
  )

  if (prevHasPlan === nextHasPlan) {
    return
  }

  store.dispatch(updateOptimizelyFeatureUser({
    user_has_plan_selected: formatUserHasPlanSelectedAttribute(nextHasPlan),
  }))
}

/**
 * Get the page view attributes for the Optimizely feature user.
 * This function retrieves the current page URL, referrer, and path from the state
 * and constructs the attributes to be sent with the page view event.
 * @param {object} state - The current application state.
 * @returns {object} - The page view attributes including URL, referrer, and path.
 */
function getPageViewAttributes (state) {
  const { resolver = Map(), optimizelyFeature = Map() } = state
  const { origin } = config

  const location = resolver.get('location') || {}
  const path = resolver.get('path') || '/'
  const oldPath = optimizelyFeature.getIn(['user', 'attributes', 'page_path']) || '/'

  const url = (global && global.location) ? global.location.href : `${createAbsolute(path)}${location.search || ''}`
  let referrer = (global && global.document) ? global.document.referrer : ''
  if (!referrer || referrer.includes(origin)) {
    referrer = createAbsolute(oldPath)
  }

  return {
    url,
    referrer,
    path,
  }
}

/**
 * Set the page viewed event for the Optimizely feature user.
 * This function retrieves the user ID and page attributes from the state,
 * and sends a page view event to the Optimizely feature service.
 * It also updates the user attributes with the current page information.
 * @param {object} store - redux store
 * @returns {void}
 */
function setPageViewed (store) {
  const state = store.getState()
  const { optimizelyFeature = Map() } = state
  const userId = optimizelyFeature.getIn(['user', 'id'])
  const pageAttributes = getPageViewAttributes(state)

  // Sent Optimizely Feature event
  if (userId) {
    setOptimizelyFeatureEvent('page_view', userId, {
      URL: pageAttributes.url,
      path: pageAttributes.path,
    })
  }

  // Update user page
  store.dispatch(updateOptimizelyFeatureUser({
    page_url: pageAttributes.url,
    page_path: pageAttributes.path,
    page_referrer: pageAttributes.referrer,
  }))
}

/**
 * Set the checkout step for the Optimizely feature user.
 * This function retrieves the current checkout step from the action payload
 * and sends a checkout step event to the Optimizely feature service.
 * @param {object} store - redux store
 * @param {object} action - redux action containing the checkout step
 * @returns {void}
 */
function setCheckoutStep (store, action) {
  const state = store.getState()
  const { optimizelyFeature = Map() } = state

  const step = _get(action, ['payload'])
  const userId = optimizelyFeature.getIn(['user', 'id'])

  if (userId && step) {
    setOptimizelyFeatureEvent('checkout_step', userId, { step })
  }
}

/**
 * Send the Optimizely feature event.
 * This function retrieves the user ID and event name from the action payload
 * and sends a feature event to the Optimizely feature service.
 * @param {object} store - redux store
 * @param {object} action - redux action containing the event name and tags
 * @returns {void}
 */
function sendOptimizelyFeatureEvent (store, action) {
  const { optimizelyFeature = Map() } = store.getState()
  const userId = optimizelyFeature.getIn(['user', 'id'])
  const eventName = _get(action, ['payload', 'eventName'])
  const tags = _get(action, ['payload', 'tags'], {})

  if (userId && eventName) {
    setOptimizelyFeatureEvent(eventName, userId, tags)
  }
}

/**
 * Set the tenure days for the Optimizely feature user.
 * This function retrieves the last transaction from the state
 * and calculates the tenure days between the last transaction and the current date.
 * @param {object} store - redux store
 * @returns {void}
 */
function setTenureDays (store) {
  const { paytrack = Map() } = store.getState()
  const createdAt = paytrack.getIn(['lastTransaction', 'createdAt'])

  // compare createdAt with current date
  const currentDate = new Date()
  const createdAtDate = new Date(createdAt)
  const tenure = currentDate.getTime() - createdAtDate.getTime()

  // transform tenure to days and round to nearest integer
  const tenureDays = Math.round(tenure / (1000 * 60 * 60 * 24))

  // set the tenure
  store.dispatch(updateOptimizelyFeatureUser({ user_tenure_days: tenureDays }))
}

/**
 * Set the signup status, this function returns true
 * if the user has recently signed up
 * @param {object} store - redux store
 * @returns {void}
 */
function setSignupStatus (store) {
  const { checkout = Map() } = store.getState()
  const orderSuccess = !!checkout.getIn(['orderData', 'success'])
  const checkoutInitiated = !!checkout.get('checkoutInitiated')

  if (orderSuccess && checkoutInitiated) {
    store.dispatch(updateOptimizelyFeatureUser({ user_new_signup: true }))
  }
}

/**
 * Set the onboarding choices for the Optimizely feature user.
 * This function retrieves the onboarding choices from the state
 * and sends a onboarding choices event to the Optimizely feature service.
 * @param {object} store - redux store
 * @returns {void}
 */
function setOnboardingChoices (store) {
  const { onboarding = Map() } = store.getState()
  const onboardingChoices = onboarding.get('onboardingChoices') || List()

  store.dispatch(updateOptimizelyFeatureUser({
    user_onboarding_choices: onboardingChoices.join(', '),
    user_onboarding_size: onboardingChoices.length || 0,
  }))
}

/**
 * Set the last plan SKU for the Optimizely feature user.
 * This function retrieves the last plan from the state
 * and sends a last plan SKU event to the Optimizely feature service.
 * @param {object} store - redux store
 * @returns {void}
 */
function setUserLastPlan (store) {
  const { userAccount = Map() } = store.getState()
  const lastPlan = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'planHistory', 0]) || Map()
  const lastPlanSku = lastPlan.get('planSku') || ''

  store.dispatch(updateOptimizelyFeatureUser({ user_last_plan_sku: lastPlanSku }))
}

/**
 * Set the plan category for the Optimizely feature user.
 * Runs when userAccount billing data arrives (userAccount may be empty on initial load).
 * @param {object} store - redux store
 * @returns {void}
 */
function setUserPlanCategory (store) {
  const { userAccount = Map() } = store.getState()
  const planCategory = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'plan', 'planCategory']) || ''

  store.dispatch(updateOptimizelyFeatureUser({ user_plan_category: planCategory }))
}

/**
 * Set the current plan SKU for the Optimizely feature user.
 * Runs when userAccount billing data arrives.
 * @param {object} store - redux store
 * @returns {void}
 */
function setUserCurrentPlan (store) {
  const { userAccount = Map() } = store.getState()
  const plan = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'plan'])
  const currentPlanSku = Map.isMap(plan) ? (plan.get('planSku') || plan.get('sku') || '') : ''

  store.dispatch(updateOptimizelyFeatureUser({ user_current_plan_sku: currentPlanSku }))
}

/**
 * Redux middleware
 * @param {import('redux').Store} store A redux store
 * @returns {import('redux').Middleware} Redux middleware
 */
export default function middleware (store) {
  return (next) => (action) => {
    const type = _get(action, 'type')
    const result = next(action)

    if (!optimizelyEnabled) {
      return result
    }

    switch (type) {
      case SET_USER_DATA_LANGUAGE:
      case SET_USER_DATA_LANGUAGE_PRIMARY:
        setUserLanguageAttribute(store, action)
        break
      case SET_AUTH_DATA:
        setUserAuth(store)
        initializeUser(store)
        setUpdatedVariation(store)
        break
      case SET_PLANS_SELECTION:
        setPlansSelection(store, action)
        syncUserHasPlanSelected(store)
        setUpdatedVariation(store)
        break
      case SET_RESOLVER_LOCATION:
        setUpdatedVariation(store)
        break
      case SET_INBOUND_TRACKING_DATA_INITIALIZED:
        setInboundTrackingAttributes(store, action)
        initializeUser(store)
        setUpdatedVariation(store)
        break
      case SET_OPTIMIZELY_FEATURE_MOUNTED:
      case SET_OPTIMIZELY_FEATURE_FLAG:
        setUpdatedVariation(store)
        break
      case SET_APP_STORE_INITIALIZED:
        initializeUser(store)
        setUpdatedVariation(store)
        break
      case SET_EVENT_PAGE_VIEWED:
        setPageViewed(store, action)
        setUpdatedVariation(store)
        break
      case SET_CHECKOUT_STEP:
        setCheckoutStep(store, action)
        break
      case SET_OPTIMIZELY_FEATURE_EVENT:
        sendOptimizelyFeatureEvent(store, action)
        break
      case SET_PAYTRACK_DATA_LAST_TRANSACTION:
        setTenureDays(store)
        break
      case SET_CHECKOUT_ORDER_DATA:
        setSignupStatus(store, action)
        break
      case SET_ONBOARDING_COMPLETED:
        setOnboardingChoices(store)
        break
      case SET_USER_ACCOUNT_DATA_BILLING_SUBSCRIPTIONS_WITH_DETAILS:
        setUserLastPlan(store)
        setUserPlanCategory(store)
        setUserCurrentPlan(store)
        break
      default:
        break
    }

    return result
  }
}
